<template>
  <div :id="`${source}_add_ringtone`">
    <CustomModal
      title="New Custom Ringtone"
      width="520px"
      :visible="visible"
      @update:visible="$emit('update:visible', $event)"
    >
      <div class="flex flex-col gap-6 proxima_nova secondary_color text-[13px]">
        <div id="ringtone_name" class="form_col">
          <span>Ringtone name</span>
          <a-input
            class="input_medium"
            v-model:value="formState.name"
            placeholder="Enter ringtone name"
            ref="ringtoneNameInputRef"
            allow-clear
          />
        </div>

        <div id="ringtone_file_section" class="flex flex-col items-center gap-2">
          <div v-if="formState.file" class="flex flex-col w-full gap-2">
            <span class="w-full">Ringtone File</span>
            <div
              class="flex flex-row items-center gap-3 justify-between p-3 bg_white_4 rounded-lg w-full"
            >
              <AudioPlayer
                :source="source"
                :audio-url="audioUrl"
                :type="formState.file?.type"
              />
              <span
                class="text-sm text_color font-semibold cursor-pointer flex-shrink-0 whitespace-nowrap"
                @click="handleReplaceClick"
              >
                Replace
              </span>
            </div>
          </div>

          <div v-else class="w-full">
            <div id="upload_ringtone" class="form_col w-full">
              <span>Ringtone File</span>
              <UploadComponent
                :source="source"
                type="audio"
                description="Only .mp3 or .wav audio files are supported (max 10 MB)"
                v-model="formState.file"
                @update:modelValue="handleFileSelection"
              />
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <a-button
            class="secondary_medium"
            @click="handleCancel"
            :disabled="formState.isLoading"
          >
            Cancel
          </a-button>
          <a-button
            class="primary_medium"
            @click="handleSubmit"
            :disabled="isSubmitDisabled"
            :loading="formState.isLoading"
          >
            {{ formState.isLoading ? "Adding" : "Add Ringtone" }}
          </a-button>
        </div>
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount, nextTick } from "vue";
import { message } from "ant-design-vue";
import CallService from "@/src/api/CallService.js";
import CustomModal from "@/src/components/CustomModal.vue";
import UploadComponent from "@/src/components/upload_components/UploadComponent.vue";
import AudioPlayer from "@/src/components/AudioPlayer.vue";
import { ringtoneFileConfig } from "@/src/constants/callConstants.js";

const props = defineProps({
  source: { type: String, required: true },
  visible: { type: Boolean, required: true },
});

const emit = defineEmits(["update:visible", "update:success"]);

const { allowedExtensions: ALLOWED_EXTENSIONS, allowedMimeTypes: ALLOWED_MIME_TYPES, maxFileSizeBytes: MAX_FILE_SIZE_BYTES } = ringtoneFileConfig;

const formState = ref({
  name: "",
  file: null,
  isLoading: false,
});
const ringtoneNameInputRef = ref(null);
const audioUrl = ref(null);

const isSubmitDisabled = computed(() => {
  const { name, file, isLoading } = formState.value;
  return !file || !name || name.trim() === "" || isLoading;
});

const releaseAudioUrl = () => {
  if (audioUrl.value) {
    URL.revokeObjectURL(audioUrl.value);
    audioUrl.value = null;
  }
};

const createAudioUrl = (file) => {
  releaseAudioUrl();
  if (file) {
    audioUrl.value = URL.createObjectURL(file);
  }
};

const resetForm = () => {
  formState.value = {
    name: "",
    file: null,
    isLoading: false,
  };
  releaseAudioUrl();
};

const stripExtension = (fileName) => {
  if (!fileName) return "";
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;
};

const validateFile = (file) => {
  if (!file) return "Please select a file";

  const fileName = (file.name || "").toLowerCase();
  const hasValidExtension = ALLOWED_EXTENSIONS.some((ext) =>
    fileName.endsWith(ext),
  );
  const hasValidMime =
    !file.type || ALLOWED_MIME_TYPES.includes(file.type.toLowerCase());

  if (!hasValidExtension || !hasValidMime) {
    return "Only .mp3 and .wav files are allowed";
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return "File size exceeds 10MB limit";
  }
  return null;
};

const handleFileSelection = (file) => {
  if (!file) {
    formState.value.file = null;
    releaseAudioUrl();
    return;
  }

  const errorMessage = validateFile(file);
  if (errorMessage) {
    formState.value.file = null;
    releaseAudioUrl();
    message.error(errorMessage);
    return;
  }

  formState.value.file = file;
  if (!formState.value.name || formState.value.name.trim() === "") {
    formState.value.name = stripExtension(file.name);
  }
  createAudioUrl(file);
};

const handleReplaceClick = () => {
  formState.value.file = null;
  releaseAudioUrl();
};

const handleCancel = () => {
  emit("update:visible", false);
};

const handleSubmit = async () => {
  const errorMessage = validateFile(formState.value.file);
  if (errorMessage) {
    message.error(errorMessage);
    return;
  }

  try {
    formState.value.isLoading = true;

    const payload = new FormData();
    payload.append("recording_name", formState.value.name.trim());
    payload.append("file", formState.value.file);

    const response = await CallService.createIncomingCallRingtone(payload);

    if (response?.ringtone) {
      emit("update:success", response.ringtone);
      emit("update:visible", false);
      message.success("Ringtone added successfully");
      resetForm();
    } else {
      throw new Error(response?.error || "Failed to add ringtone");
    }
  } catch (error) {
    console.error("Error creating ringtone:", error);
    message.error(
      error?.response?.data?.error ||
        error?.message ||
        "An error occurred while creating ringtone",
    );
  } finally {
    formState.value.isLoading = false;
  }
};

watch(
  () => props.visible,
  (newVal) => {
    if (!newVal) {
      resetForm();
    } else {
      nextTick(() => {
        ringtoneNameInputRef.value?.focus?.();
      });
    }
  },
);

onBeforeUnmount(() => {
  releaseAudioUrl();
});
</script>
